import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash2, Save, Send } from "lucide-react";
import { toast } from "sonner";

export default function OrderCreatePage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState({ customer_id: "", company_id: "", type: "NON_ORGANIC", notes: "" });
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/customers"), api.get("/admin/companies")])
      .then(([c, co]) => { setCustomers(c.data.items || []); setCompanies(co.data || []); })
      .catch(() => {});
  }, []);

  const addLine = () => setLines([...lines, { item_code: "", item_name: "", batch: "", quantity: 1, unit_price: 0, discount_pct: 0 }]);
  const updateLine = (i, f, v) => { const u = [...lines]; u[i] = { ...u[i], [f]: v }; setLines(u); };
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));
  const lineTotal = (l) => (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0) * (1 - (parseFloat(l.discount_pct) || 0) / 100);
  const grandTotal = lines.reduce((s, l) => s + lineTotal(l), 0);

  const handleSave = async (submit = false) => {
    if (!form.customer_id || !form.company_id) { toast.error("Select customer and company"); return; }
    if (lines.length === 0) { toast.error("Add at least one line item"); return; }
    setSaving(true);
    try {
      const payload = { ...form, lines: lines.map((l) => ({ ...l, quantity: parseFloat(l.quantity) || 0, unit_price: parseFloat(l.unit_price) || 0, discount_pct: parseFloat(l.discount_pct) || 0 })) };
      const res = await api.post("/orders", payload);
      if (submit) { await api.post(`/orders/${res.data.id}/submit`); toast.success("Order created and submitted"); }
      else toast.success("Order saved as draft");
      navigate("/orders");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.code === "STOCK_INSUFFICIENT") {
        const warnings = detail.stock_warnings || [];
        toast.error(`Stock insufficient: ${warnings.map(w => `${w.item_code} (need ${w.requested}, have ${w.available})`).join(", ")}`, { duration: 6000 });
      } else {
        toast.error(typeof detail === "string" ? detail : detail?.message || "Failed to save order");
      }
    }
    setSaving(false);
  };

  return (
    <div data-testid="order-create-page" className="space-y-6 max-w-5xl">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#09090B]" style={{ fontFamily: "Chivo, sans-serif" }}>New Sales Order</h1>
        <p className="text-sm text-gray-500 mt-1">Create a new sales order</p>
      </div>

      <Card className="border border-gray-200 shadow-sm rounded-sm">
        <CardContent className="p-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500">Customer</Label>
              <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                <SelectTrigger data-testid="order-customer-select" className="mt-1.5 rounded-sm h-9"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>{customers.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name} ({c.code})</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500">Company</Label>
              <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger data-testid="order-company-select" className="mt-1.5 rounded-sm h-9"><SelectValue placeholder="Select company" /></SelectTrigger>
                <SelectContent>{companies.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="mt-1.5 rounded-sm h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="NON_ORGANIC">Non-Organic (Import)</SelectItem>
                  <SelectItem value="ORGANIC">Organic</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 rounded-sm" rows={2} />
          </div>
        </CardContent>
      </Card>

      <Card className="border border-gray-200 shadow-sm rounded-sm">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold uppercase tracking-[0.15em] text-gray-500">Line Items</h3>
            <Button data-testid="add-order-line" variant="outline" size="sm" onClick={addLine} className="rounded-sm"><Plus size={14} className="mr-1" /> Add Item</Button>
          </div>
          {lines.length === 0 && <p className="text-sm text-gray-400 text-center py-6">No line items yet.</p>}
          {lines.map((line, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 mb-3 items-end">
              <div className="col-span-2">{idx === 0 && <Label className="text-[10px] uppercase tracking-wider text-gray-400">Item Code</Label>}<Input value={line.item_code} onChange={(e) => updateLine(idx, "item_code", e.target.value)} className="rounded-sm h-8 text-sm" /></div>
              <div className="col-span-3">{idx === 0 && <Label className="text-[10px] uppercase tracking-wider text-gray-400">Item Name</Label>}<Input value={line.item_name} onChange={(e) => updateLine(idx, "item_name", e.target.value)} className="rounded-sm h-8 text-sm" /></div>
              <div className="col-span-1">{idx === 0 && <Label className="text-[10px] uppercase tracking-wider text-gray-400">Batch</Label>}<Input value={line.batch} onChange={(e) => updateLine(idx, "batch", e.target.value)} className="rounded-sm h-8 text-sm" /></div>
              <div className="col-span-1">{idx === 0 && <Label className="text-[10px] uppercase tracking-wider text-gray-400">Qty</Label>}<Input type="number" value={line.quantity} onChange={(e) => updateLine(idx, "quantity", e.target.value)} className="rounded-sm h-8 text-sm" /></div>
              <div className="col-span-2">{idx === 0 && <Label className="text-[10px] uppercase tracking-wider text-gray-400">Unit Price</Label>}<Input type="number" step="0.01" value={line.unit_price} onChange={(e) => updateLine(idx, "unit_price", e.target.value)} className="rounded-sm h-8 text-sm" /></div>
              <div className="col-span-1">{idx === 0 && <Label className="text-[10px] uppercase tracking-wider text-gray-400">Disc %</Label>}<Input type="number" value={line.discount_pct} onChange={(e) => updateLine(idx, "discount_pct", e.target.value)} className="rounded-sm h-8 text-sm" /></div>
              <div className="col-span-1 text-right">{idx === 0 && <Label className="text-[10px] uppercase tracking-wider text-gray-400">Total</Label>}<div className="h-8 flex items-center justify-end text-sm font-medium">{lineTotal(line).toFixed(2)}</div></div>
              <div className="col-span-1 flex justify-end"><Button variant="ghost" size="sm" onClick={() => removeLine(idx)} className="h-8 w-8 p-0 text-red-500"><Trash2 size={14} /></Button></div>
            </div>
          ))}
          {lines.length > 0 && (
            <div className="border-t border-gray-200 pt-3 mt-3 flex justify-end">
              <span className="text-xs uppercase tracking-wider text-gray-500 mr-4">Grand Total</span>
              <span className="text-xl font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>{grandTotal.toFixed(2)}</span>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate("/orders")} className="rounded-sm">Cancel</Button>
        <Button variant="outline" onClick={() => handleSave(false)} disabled={saving} className="rounded-sm"><Save size={14} className="mr-1.5" /> Save Draft</Button>
        <Button onClick={() => handleSave(true)} disabled={saving} className="bg-[#0052CC] hover:bg-[#003D99] rounded-sm"><Send size={14} className="mr-1.5" /> Save & Submit</Button>
      </div>
    </div>
  );
}
